import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Eye, MousePointer, ShoppingCart, CreditCard, TrendingUp, 
  RefreshCw, BarChart3, Users, Clock, Target, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PageAnalyticsManagement = () => {
  const [analytics, setAnalytics] = useState(null);
  const [cartAnalytics, setCartAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, cartRes] = await Promise.all([
        axios.get(`${API}/admin/page-analytics`),
        axios.get(`${API}/admin/cart-analytics`)
      ]);
      setAnalytics(analyticsRes.data);
      setCartAnalytics(cartRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Loading analytics...</p>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const planStats = analytics?.planStats || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent flex items-center">
          <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
          Page Analytics & Conversion
        </h2>
        <Button
          onClick={fetchAnalytics}
          variant="outline"
          className="border-white/20 hover:bg-slate-100"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-blue-500/30 hover:scale-105 transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Page Views</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">{summary.totalPageViews || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-pink-500/10 border-slate-200 hover:scale-105 transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Product Page Views</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{summary.productPageViews || 0}</p>
              </div>
              <Target className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30 hover:scale-105 transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Button Clicks</p>
                <p className="text-2xl font-bold text-orange-400 mt-1">{summary.buttonClicks || 0}</p>
              </div>
              <MousePointer className="w-8 h-8 text-orange-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500/10 to-blue-500/10 border-yellow-500/30 hover:scale-105 transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Cart Adds</p>
                <p className="text-2xl font-bold text-yellow-400 mt-1">{summary.cartAdds || 0}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-yellow-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-400/10 to-blue-700/10 border-cyan-500/30 hover:scale-105 transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Checkout Starts</p>
                <p className="text-2xl font-bold text-cyan-400 mt-1">{summary.checkoutStarts || 0}</p>
              </div>
              <CreditCard className="w-8 h-8 text-cyan-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-200 hover:scale-105 transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Purchases</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{summary.purchases || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Rate Card */}
      <Card className="bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-teal-500/10 border-green-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Overall Conversion Rate</h3>
              <p className="text-sm text-slate-500">From product page view to purchase</p>
            </div>
            <div className="text-right">
              <p className="text-3xl md:text-5xl font-bold text-green-600">{summary.overallConversion || 0}%</p>
              <p className="text-sm text-slate-500">conversion</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan-wise Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        {['Basic', 'Advanced', 'Professional'].map((plan) => {
          const stats = planStats[plan] || {};
          const colors = {
            Basic: 'from-blue-500 to-blue-500',
            Advanced: 'from-blue-500 to-pink-500',
            Professional: 'from-orange-500 to-red-500'
          };
          
          return (
            <Card key={plan} className={`bg-gradient-to-br ${colors[plan]}/10 border-white/10`}>
              <CardHeader>
                <CardTitle className={`text-lg bg-gradient-to-r ${colors[plan]} bg-clip-text text-transparent`}>
                  {plan} Plan Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-slate-800">{stats.clicks || 0}</p>
                    <p className="text-xs text-slate-500">Clicks</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-slate-800">{stats.carts || 0}</p>
                    <p className="text-xs text-slate-500">Cart Adds</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-slate-800">{stats.checkouts || 0}</p>
                    <p className="text-xs text-slate-500">Checkouts</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-2xl font-bold text-slate-800">{stats.purchases || 0}</p>
                    <p className="text-xs text-slate-500">Purchases</p>
                  </div>
                </div>
                <div className="p-3 bg-green-500/10 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Conversion Rate</span>
                    <span className="text-xl font-bold text-green-600">{stats.conversionRate || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Cart Analytics */}
      <Card className="bg-white border-white/10">
        <CardHeader>
          <CardTitle className="text-lg text-yellow-400 flex items-center">
            <ShoppingCart className="w-5 h-5 mr-2" />
            Cart Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-2xl md:text-3xl font-bold text-blue-400">{cartAnalytics?.totalCarts || 0}</p>
              <p className="text-sm text-slate-500">Total Carts</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-2xl md:text-3xl font-bold text-green-600">{cartAnalytics?.completedPurchases || 0}</p>
              <p className="text-sm text-slate-500">Completed</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-2xl md:text-3xl font-bold text-yellow-400">{cartAnalytics?.pendingCarts || 0}</p>
              <p className="text-sm text-slate-500">Pending Carts</p>
            </div>
            <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/30">
              <p className="text-2xl md:text-3xl font-bold text-red-400">{cartAnalytics?.abandonmentRate || 0}%</p>
              <p className="text-sm text-slate-500">Abandonment Rate</p>
            </div>
          </div>

          {/* Pending Carts List */}
          {cartAnalytics?.pendingCartDetails?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-600 mb-3 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2 text-yellow-400" />
                Recent Pending Carts (Not Purchased)
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {cartAnalytics.pendingCartDetails.map((cart, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-slate-800 font-medium">{cart.planName || 'Unknown Plan'}</p>
                        <p className="text-xs text-slate-500">Session: {cart.sessionId?.slice(0, 12)}...</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">
                        {cart.createdAt ? new Date(cart.createdAt).toLocaleString() : '-'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Events */}
      <Card className="bg-white border-white/10">
        <CardHeader>
          <CardTitle className="text-lg text-cyan-400 flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            Recent Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {analytics?.recentEvents?.slice(0, 20).map((event, idx) => {
              const eventColors = {
                page_view: 'text-blue-400 bg-blue-500/20',
                button_click: 'text-orange-400 bg-orange-500/20',
                cart_add: 'text-yellow-400 bg-yellow-500/20',
                checkout_start: 'text-cyan-400 bg-cyan-500/20',
                purchase: 'text-green-600 bg-green-100'
              };
              const eventIcons = {
                page_view: Eye,
                button_click: MousePointer,
                cart_add: ShoppingCart,
                checkout_start: CreditCard,
                purchase: TrendingUp
              };
              const Icon = eventIcons[event.eventType] || Eye;
              const colorClass = eventColors[event.eventType] || 'text-slate-500 bg-slate-500/20';
              
              return (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-slate-100 transition-all">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-medium capitalize">{event.eventType?.replace('_', ' ')}</p>
                      <p className="text-xs text-slate-500">{event.page} {event.planName ? `• ${event.planName}` : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">
                      {event.createdAt ? new Date(event.createdAt).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PageAnalyticsManagement;
